import { useEffect } from 'react';

import { apiBaseUrl } from './api';

const LOG_EVENT_PATH = '/api/misc/log-event';

// Fire-and-forget behavioural event for the CRM's member-activity views.
// sendBeacon (not fetch) because these often fire as the page is unloading, and
// a fetch started during unload is routinely cancelled. JSON, since a beacon can
// only send a Blob/string. Cookies ride along, so the server resolves the member.
export const logEvent = payload => {
  if (typeof window === 'undefined') {
    return;
  }

  const url = `${apiBaseUrl()}${LOG_EVENT_PATH}`;
  const body = JSON.stringify(payload);

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) {
        return;
      }
    }

    window
      .fetch(url, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body,
      })
      .catch(() => {});
  } catch (e) {
    // Tracking must never surface an error to the member.
  }
};

/**
 * Logs one timed view event when the view ends (unmount, tab hide, page unload,
 * or when subjectKey changes).
 *
 * subjectKey (e.g. the listing id) is required: the router keys on route name,
 * so listing A -> B reuses this component. Without it the effect never restarts,
 * so A goes unlogged and B's duration starts from A.
 */
export const useTrackedView = ({ eventType, data, subjectKey, enabled = true }) => {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return undefined;
    }

    // Snapshot in the effect's closure so the event describes the view that just
    // ended, not whatever the next render moved on to.
    const viewData = data;
    const startedAt = Date.now();
    let sent = false;

    const send = () => {
      if (sent) {
        return;
      }
      sent = true;
      logEvent({
        eventType,
        ...viewData,
        durationSeconds: Math.round((Date.now() - startedAt) / 1000),
      });
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        send();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', send);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', send);
      send();
    };
    // `data` is deliberately not a dep — subjectKey identifies the view, and a new
    // object each render would restart tracking constantly.
  }, [enabled, eventType, subjectKey]);
};
